import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class ImageUtilsService {
    /**
     * Converts a PNG image to a JPEG image with the given quality.
     *
     * The image is scaled to fit within the given maximum width and height.
     * If the image is larger than the maximum dimensions, it is scaled down to fit.
     * If the image is smaller than the maximum dimensions, it is scaled up to fit.
     *
     * @param {HTMLImageElement} pngImage - The PNG image element to be converted.
     * @param {number} width - The desired width of the output JPEG image.
     * @param {number} height - The desired height of the output JPEG image.
     * @param {number} [jpegQuality=0.95] - Optional parameter specifying the desired quality of the JPEG image, between 0 and 1.
     * @return {Promise<string>} A promise that resolves to a Base64-encoded data URL of the converted JPEG image.
     */
    public async convertPngToJpegAutoscale(
        pngImage: HTMLImageElement,
        maxWidth = 300,
        maxHeight = 100,
        jpegQuality = 0.95,
    ): Promise<string> {
        const ratio = Math.min(maxWidth / pngImage.width, maxHeight / pngImage.height, 1);
        const width = pngImage.width * ratio;
        const height = pngImage.height * ratio;

        return this.convertPngToJpeg(pngImage, width, height, jpegQuality);
    }

    /**
     * Converts a PNG image to a JPEG image with specified dimensions and quality.
     *
     * @param {HTMLImageElement} pngImage - The PNG image element to be converted.
     * @param {number} width - The desired width of the output JPEG image.
     * @param {number} height - The desired height of the output JPEG image.
     * @param {number} [jpegQuality=0.95] - Optional parameter specifying the desired quality of the JPEG image, between 0 and 1.
     * @return {Promise<string>} A promise that resolves to a Base64-encoded data URL of the converted JPEG image.
     */
    public async convertPngToJpeg(
        pngImage: HTMLImageElement,
        width: number,
        height: number,
        jpegQuality = 0.95,
    ): Promise<string> {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context ist null.');

        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(pngImage, 0, 0, width, height);

        return canvas.toDataURL('image/jpeg', jpegQuality);
    }

    /**
     * Converts a PNG image to a JPEG format suitable for printing, with specified dimensions and resolution.
     *
     * @param {HTMLImageElement} pngImage - The PNG image to be converted.
     * @param {number} widthInMm - The desired width of the image in millimeters.
     * @param {number} heightInMm - The desired height of the image in millimeters.
     * @param {number} [dpi=300] - The dots per inch (DPI) resolution for the output image. Defaults to 300 DPI.
     * @param {number} [jpegQuality=0.95] - The quality of the resulting JPEG image, ranging from 0 to 1. Defaults to 0.95.
     * @return {Promise<string>} A promise that resolves to a base64-encoded string of the JPEG image.
     */
    public async convertPngToJpegForPrint(
        pngImage: HTMLImageElement,
        widthInMm: number,
        heightInMm: number,
        dpi = 300,
        jpegQuality = 0.95,
    ): Promise<string> {
        const width = Math.round((widthInMm * dpi) / 25.4);
        const height = Math.round((heightInMm * dpi) / 25.4);
        return this.convertPngToJpeg(pngImage, width, height, jpegQuality);
    }

    /**
     * Determines the type of the image from a given base64 encoded string.
     *
     * @param {string} base64String - The base64 encoded string representing the image data.
     * @return {string} The MIME type of the image, such as 'image/png' or 'image/jpeg'.
     */
    public getImageType(base64String: string): string {
        const extracted = this.extractImagesFromBase64String(base64String);
        return extracted[0]?.type;
    }

    /**
     * Determines the type of the image from a given base64 encoded string.
     * Returns the value to be used with the format param of jsPdf.addImage() method.
     *
     * @param {string} base64String - The base64 encoded string representing the image data.
     * @return {string} The type of the image, PNG, JPG, JPEG, etc.
     */
    public getImageTypeForPdf(base64String: string): string {
        const type = this.getImageType(base64String);
        return type.replace('image/', '').toUpperCase();
    }

    // Note: Kept for potential future use as it was implemented in analyze-detail.component.ts
    private extractImagesFromBase64String(base64String: string): { type: string; data: string }[] {
        const imageRegex = /data:(image\/.*);base64,([a-zA-Z0-9+/]+={0,2})/g;
        const images = [];
        let match;
        while ((match = imageRegex.exec(base64String)) !== null) {
            images.push({
                type: match[1], // 'png', 'jpeg', 'jpg'
                data: match[2], // Base64 encoded image data
                // fullMatch: match[0], // Full string match if needed
            });
        }
        return images;
    }
}
